<template>
  <b-card
    :id="sectionId"
    :class="`delivery-section${isError ? '--error' : ''}`"
    header-class="delivery-section__header-card"
    body-class="delivery-section__body-card"
  >
    <template #header>
      <div class="delivery-section__title-container">
        <slot name="checkbox" :id="`delivery-${id}`" />
        <label :for="`delivery-${id}`">
          <span>{{ $t('delivery.title.delivery') }} {{ index }}</span>
          <span>|</span>
          <span>{{ totalWeight }} Kg {{ totalVolume }} Cbm</span>
        </label>
      </div>
      <div class="delivery-section__actions-container">
        <b-button variant="outline" class="btn __delete-btn" :disabled="itemsLength == 1">
          <i class="icon-outline_trash" @click="$emit('click-delete')" />
        </b-button>
        <b-button
          v-if="hasFeatureScheduleFoFromBulkDoMGM"
          variant="outline"
          class="btn __duplicate-btn"
          :disabled="!canDuplicate"
          @click="$emit('click-duplicate')"
        >
          <i class="icon-outline_copy" />
        </b-button>
        <b-button variant="outline" class="btn __collapse-btn" @click="isVisible = !isVisible">
          <i :class="`icon-outline_chevron-${isVisible ? 'up' : 'down'}`" />
        </b-button>
      </div>
    </template>
    <b-collapse v-model="isVisible">
      <div class="delivery-section__body-container">
        <b-row>
          <b-col cols="12" md="6" class="delivery-section__address-container">
            <span>{{ $t('delivery.originLocation') }}</span>
            <b-row>
              <b-col v-for="field in originFields" v-if="field.isShown !== false" cols="12" md="6">
                <AddressValueHelp
                  v-if="field.name == 'origin_address_id'"
                  :id="id"
                  :field="field"
                  :value="formValues?.[field.name]"
                  :fullObject="fullObjects?.[field.name]"
                  :otherAdressFullObject="fullObjects?.destination_address_id"
                  @input="handleUpdateFormValues({ key: field.name, value: $event })"
                  @update:fullObject="
                    handleUpdateFullObjects({ key: field.name, value: $event })
                  "
                />
                <FormulateInput
                  v-else
                  v-bind="field"
                  class="p-0"
                  validation-behavior="live"
                  :name="`${field.name}-${id}`"
                  :help="field?.validation?.includes('required')"
                  :value="formValues?.[field.name]"
                  :fullObject="fullObjects?.[field.name]"
                  @input="handleUpdateFormValues({ key: field.name, value: $event })"
                  @update:fullObject="
                    handleUpdateFullObjects({ key: field.name, value: $event })
                  "
                />
              </b-col>
            </b-row>
          </b-col>

          <b-col cols="12" md="6" class="delivery-section__address-container">
            <span>{{ $t('delivery.destinationLocation') }}</span>
            <b-row>
              <b-col v-for="field in destinationFields" v-if="field.isShown !== false" cols="12" md="6">
                <AddressValueHelp
                  v-if="field.name == 'destination_address_id'"
                  :id="id"
                  :field="field"
                  :value="formValues?.[field.name]"
                  :fullObject="fullObjects?.[field.name]"
                  :otherAdressFullObject="fullObjects?.origin_address_id"
                  @input="handleUpdateFormValues({ key: field.name, value: $event })"
                  @update:fullObject="
                    handleUpdateFullObjects({ key: field.name, value: $event })
                  "
                />
                <FormulateInput
                  v-else
                  v-bind="field"
                  class="p-0"
                  validation-behavior="live"
                  :name="`${field.name}-${id}`"
                  :help="field?.validation?.includes('required')"
                  :value="formValues?.[field.name]"
                  :fullObject="fullObjects?.[field.name]"
                  @input="handleUpdateFormValues({ key: field.name, value: $event })"
                  @update:fullObject="
                    handleUpdateFullObjects({ key: field.name, value: $event })
                  "
                />
              </b-col>
            </b-row>
          </b-col>
        </b-row>

        <LoadsTable :id="id" />
      </div>
    </b-collapse>
  </b-card>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import { EventBus } from '../lib/event-bus.js'

import AddressValueHelp from './wrapper/AddressValueHelp'
import LoadsTable from './LoadsTable'

import { getOriginFields, getDestinationFields } from '../lib/fields/delivery-detail-fields.js'
import { getLoadFields } from '../lib/fields/load-fields.js'
import { Helper } from '@/mixin/helper'

export default {
  components: {
    AddressValueHelp,
    LoadsTable,
  },
  props: {
    id: {
      type: Number,
    },
    index: {
      type: Number,
    },
    itemsLength: {
      type: Number,
    },
    selected: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['click-delete', 'click-duplicate', 'update:formValues', 'update:fullObjects'],
  data() {
    return {
      checked: false,
      isVisible: true,
    }
  },
  computed: {
    ...mapState('delivery', ['bulkIdsError', 'bulkItemValues', 'bulkItemObjects', 'bulkLoadValues', 'bulkLoadObjects']),
    ...mapGetters({
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
    originFields() {
      return getOriginFields((key, params) => this.$t(key, params))
    },
    destinationFields() {
      return getDestinationFields((key, params) => this.$t(key, params))
    },
    loadFields() {
      return getLoadFields((key, params) => this.$t(key, params))
    },
    formValues() {
      return this.bulkItemValues?.[this.id]
    },
    fullObjects() {
      return this.bulkItemObjects?.[this.id]
    },
    sectionId() {
      return `delivery-section-${this.id}`
    },
    totalWeight() {
      let total = 0

      const loads = this.bulkLoadValues?.[this.id] || []

      if (loads.length > 0) {
        loads.forEach((loads, index) => {
          total += (loads.quantity * this.bulkLoadObjects[this.id]?.[index]?.uom.weight || 0)
        })
      }

      return total || 0
    },
    totalVolume() {
      let total = 0

      const loads = this.bulkLoadValues?.[this.id] || []

      if (loads.length > 0) {
        loads.forEach((loads, index) => {
          total += (loads.quantity * this.bulkLoadObjects[this.id]?.[index]?.uom.volume || 0)
        })
      }

      return total || 0
    },
    isError() {
      return this.bulkIdsError[this.id] || false
    },
    hasFeatureScheduleFoFromBulkDoMGM() {
      const companyId = Helper.methods.getCompanyId()
      const data = this.getFeatureFlagValue('customFeature.mgm')
      return (
        Array.isArray(data?.company_id) &&
        data.company_id.includes(companyId) &&
        data?.features?.schedule_fo_from_bulk_do
      )
    },
    canDuplicate() {
      if (!this.hasFeatureScheduleFoFromBulkDoMGM) return false

      const detailFields = [...this.originFields, ...this.destinationFields]
      const hasFilledDetailValues = detailFields.some((field) =>
        this.isFieldFilled(this.formValues?.[field.name])
      )
      const loads = this.bulkLoadValues?.[this.id] || []
      const hasFilledLoadValues = loads.some((load) => this.hasMeaningfulLoadValues(load))

      return hasFilledDetailValues || hasFilledLoadValues
    },
  },
  watch: {
    selected(newValue) {
      if (newValue == this.checked) return

      this.checked = newValue
    },
  },
  methods: {
    isFieldFilled(value) {
      if (Array.isArray(value)) return value.length > 0
      if (typeof value === 'string') return value.trim() !== ''

      return value !== null && value !== undefined && value !== false
    },
    hasMeaningfulLoadValues(load = {}) {
      const editableLoadFields = this.loadFields.filter((field) => field?.isEditable)

      return editableLoadFields.some((field) => {
        const value = load?.[field.name]

        if (field.name === 'quantity') {
          return this.isFieldFilled(value) && Number(value) !== 1
        }

        return this.isFieldFilled(value)
      })
    },
    handleUpdateFormValues({ key, value }) {
      const newValues = { ...this.bulkItemValues[this.id], [key]: value }

      this.$store.dispatch('delivery/setBulkItemValues', { key: this.id, value: newValues })
      EventBus.$emit('remove-error', `${key}-${this.id}`)
    },
    handleUpdateFullObjects({ key, value }) {
      const newValues = { ...this.bulkItemObjects[this.id], [key]: value }
      this.$store.dispatch('delivery/setBulkItemObjects', { key: this.id, value: newValues })
    },
  },
}
</script>

<style lang="scss" scoped>
.delivery-section {
  &--error {
    border: 1px solid $red;
  }

  &__header-card {
    background-color: $mep-secondary-100;
    border-radius: 4px 4px 0 0;
    font-size: 16px;
    padding: 16px;

    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  &__title-container {
    display: flex;
    gap: 12px;
    align-items: center;

    label {
      display: flex;
      gap: 12px;
      margin: 0;
    }
  }

  &__actions-container {
    display: flex;
    gap: 20px;

    .btn {
      padding: 0;

      &:hover {
        border-color: transparent;
      }
    }

    .__delete-btn {
      i:before {
        color: $red;
        font-size: 24px;
      }

      &.disabled {
        opacity: $btn-disabled-opacity;
        cursor: not-allowed;
        border-color: transparent;

        i:before {
          color: $mep-color-text-300;
        }
      }
    }

    .__duplicate-btn {
      i:before {
        color: $menu-item-color;
        font-size: 24px;
      }

      &.disabled {
        opacity: $btn-disabled-opacity;
        cursor: not-allowed;
        border-color: transparent;

        i:before {
          color: $mep-color-text-300;
        }
      }
    }

    .__collapse-btn i:before {
      font-size: 32px;
    }
  }

  &__body-card {
    padding: 0;
  }

  &__body-container {
    padding: 16px;
  }

  &__address-container {
    margin-top: 0;
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__address-container {
    margin: 8px 0;
    span {
      font-size: 16px;
      color: $mep-color-text-400;
    }
  }
}
</style>